import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ProgressIndicator from "../ProgressIndicator";
import { WizardStepError } from "../WizardStepError";
import { WizardStepSkeleton } from "../WizardStepSkeleton";
import GoalActions from "./components/GoalActions";
import GoalList from "./components/GoalList";
import { useGoalManagement } from "./hooks/useGoalManagement";
import { useGoalValidation } from "./hooks/useGoalValidation";

interface GoalsProps {
  handleNext: () => void;
  handlePrevious: () => void;
  isUserEnrolled?: boolean;
}

const Goals: React.FC<GoalsProps> = ({
  handleNext,
  handlePrevious,
  isUserEnrolled,
}) => {
  const { t } = useTranslation();
  const {
    goals,
    loading,
    isSubmitting,
    error,
    isSuccess,
    handleFetchGoalsFromAPI,
    handleGoalChange,
    processAddOrUpdateGoals,
  } = useGoalManagement();

  const { validateAndProcessGoals } = useGoalValidation();

  useEffect(() => {
    handleFetchGoalsFromAPI();
  }, [handleFetchGoalsFromAPI]);

  const handleAddOrUpdateGoal = async () => {
    await validateAndProcessGoals(goals, error, async (validGoals) => {
      const success = await processAddOrUpdateGoals(validGoals);
      if (success && isSuccess) {
        handleNext();
      }
      return success;
    });
  };

  return (
    <>
      <div className="w-full mx-auto font-outfit sm:w-[640px]">
        <ProgressIndicator />

        {!isUserEnrolled && (
          <div className="flex flex-col gap-[10px]">
            <h4 className="font-semibold">{t("onboarding.goals.sub_title")}</h4>
            <p className="mb-6">{t("onboarding.goals.description")}</p>
          </div>
        )}

        {loading ? (
          <WizardStepSkeleton />
        ) : error ? (
          <WizardStepError step={2} handlePrevious={handlePrevious} />
        ) : (
          <>
            <GoalList
              goals={goals}
              isUserEnrolled={isUserEnrolled}
              onGoalChange={handleGoalChange}
            />

            <GoalActions
              isUserEnrolled={isUserEnrolled}
              isSubmitting={isSubmitting}
              onNext={handleAddOrUpdateGoal}
              onPrevious={handlePrevious}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Goals;
